import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./leafletIcons";
import { Link } from "react-router-dom";
import { API_URL as SERVER } from "../config";

export interface MapSauna {
  id: number;
  name: string;
  pricePerHourCents: number;
  latitude: number | null;
  longitude: number | null;
  saunaImagesBySaunaId?: { nodes: { url: string }[] };
}

interface BoundsControllerProps {
  saunas: MapSauna[];
}

function BoundsController({ saunas }: BoundsControllerProps) {
  const map = useMap();
  useEffect(() => {
    const pins = saunas.filter(s => s.latitude != null && s.longitude != null);
    if (pins.length === 0) return;
    const bounds = L.latLngBounds(pins.map(s => [s.latitude!, s.longitude!] as [number, number]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [saunas, map]);
  return null;
}

interface Props {
  saunas: MapSauna[];
  hoveredId?: number | null;
}

function priceIcon(cents: number, highlighted: boolean): L.DivIcon {
  const label = `$${Math.round(cents / 100)}/hr`;
  const bg = highlighted ? "#2563eb" : "#fff";
  const color = highlighted ? "#fff" : "#111827";
  const border = highlighted ? "#2563eb" : "#d1d5db";
  return L.divIcon({
    html: `<div style="background:${bg};color:${color};border:1.5px solid ${border};padding:4px 9px;border-radius:14px;font-size:12px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.22)">${label}</div>`,
    className: "",
    iconSize: [72, 28],
    iconAnchor: [36, 28],
    popupAnchor: [0, -30],
  });
}

export default function ResultsMap({ saunas, hoveredId }: Props) {
  const pins = saunas.filter(s => s.latitude != null && s.longitude != null);
  const center: [number, number] = pins.length > 0
    ? [pins[0].latitude!, pins[0].longitude!]
    : [45, -93];

  return (
    <MapContainer center={center} zoom={8} style={{ height: "100%", width: "100%", borderRadius: 12 }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <BoundsController saunas={pins} />
      {pins.map(s => {
        const thumb = s.saunaImagesBySaunaId?.nodes?.[0]?.url;
        return (
          <Marker
            key={s.id}
            position={[s.latitude!, s.longitude!]}
            icon={priceIcon(s.pricePerHourCents, s.id === hoveredId)}
          >
            <Popup>
              <Link to={`/saunas/${s.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                {thumb && (
                  <img src={`${SERVER}${thumb}`} alt={s.name} style={{ width: 160, height: 100, objectFit: "cover", borderRadius: 6, display: "block", marginBottom: 6 }} />
                )}
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  ${(s.pricePerHourCents / 100).toFixed(2)} / hr
                </div>
              </Link>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}
